import { useState } from 'react';
import { useNavigate, Navigate, Link } from 'react-router-dom';
import { useAuth, homeFor } from '../context/AuthContext.jsx';
import CubMascot from '../components/CubMascot.jsx';
import CyberCubLogo from '../components/CyberCubLogo.jsx';
import { Alert, Spinner } from '../components/ui.jsx';
import ThemeToggle from '../components/ThemeToggle.jsx';
import HelpBot from '../components/HelpBot.jsx';
import { useFieldCheck, countProblems } from '../lib/fields.jsx';

/*
 * What has to be true before it is worth asking the server. See
 * lib/fields.jsx for how these are used.
 *
 * Deliberately thin. Sign-in is not the place to tell somebody their
 * password is too short - the account may predate the rule, and it is
 * not this form's business what shape the right answer is. It only
 * checks that there is something to send.
 */
const CHECKS = {
  userId: (v) => (v.userId.trim() ? null : 'Type your email, or the User ID your teacher gave you.'),
  password: (v) => (v.password ? null : 'Type your password.'),
};
const FIELD_ORDER = ['userId', 'password'];

/* The second step has a field of its own, and its own rule. */
const CODE_CHECKS = {
  code: (v) => {
    const entered = v.code.trim();
    if (!entered) return v.useRecovery ? 'Type one of your recovery codes.' : 'Type the 6-digit code.';
    const need = v.useRecovery ? 8 : 6;
    return entered.length >= need
      ? null
      : v.useRecovery
        ? 'A recovery code is a bit longer than that — check you have the whole one.'
        : 'That code is six digits. Type all six.';
  },
};
const CODE_ORDER = ['code'];

export default function Login() {
  const { user, login, completeTotp } = useAuth();
  const navigate = useNavigate();

  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // Set once a superadmin's password is accepted but a code is still
  // needed. The password is dropped at that point - it is not wanted
  // again, and should not sit in memory behind the second step.
  const [needsCode, setNeedsCode] = useState(false);
  const [code, setCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);

  const fields = useFieldCheck(CHECKS, FIELD_ORDER);
  const codeField = useFieldCheck(CODE_CHECKS, CODE_ORDER);

  if (user) return <Navigate to={homeFor(user.role)} replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const bad = fields.checkAll({ userId, password });
    if (Object.keys(bad).length) {
      setError(countProblems(bad));
      return;
    }

    setBusy(true);
    try {
      const me = await login(userId.trim(), password);
      if (me.mfaRequired) {
        setNeedsCode(true);
        setPassword('');
        fields.clear();
        return;
      }
      navigate(me.mustChangePassword ? '/account' : homeFor(me.role), { replace: true });
    } catch (err) {
      /*
       * Both boxes are marked, never one.
       *
       * The server answers a wrong name and a wrong password with the
       * same sentence on purpose, so that a stranger cannot use this
       * form to find out whose accounts exist. Reddening only one of
       * them here would hand back exactly what the server refused to
       * say. So both go red, the message stays the one the server
       * chose, and the cursor lands back in the password - which is
       * the box that was just emptied, and the likelier mistake.
       */
      fields.mark(
        {
          userId: true,
          password: 'One of these two is not right. Check both and try again.',
        },
        'password',
      );
      setError(err.message);
      setPassword('');
    } finally {
      setBusy(false);
    }
  };

  const onSubmitCode = async (e) => {
    e.preventDefault();
    setError('');

    const bad = codeField.checkAll({ code, useRecovery });
    if (Object.keys(bad).length) {
      setError(countProblems(bad));
      return;
    }

    setBusy(true);
    try {
      const me = await completeTotp(
        useRecovery ? { recoveryCode: code.trim() } : { code: code.trim() },
      );
      navigate(me.mustChangePassword ? '/account' : homeFor(me.role), { replace: true });
    } catch (err) {
      codeField.mark({ code: err.message });
      setError(err.message);
      setCode('');
      // The challenge only lasts a few minutes; once it lapses the
      // password has to be entered again.
      if (err.status === 401 && /sign in again/i.test(err.message)) setNeedsCode(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-screen">
      {/* Sign-in sits outside the app shell, so the toggle floats. */}
      <ThemeToggle className="theme-toggle-floating" />
      <div className="login-blobs" aria-hidden="true">
        <span /><span /><span /><span />
      </div>

      <div className="login-grid">
        {/* ---------- welcome side ---------- */}
        <section className="login-welcome">
          <div className="row gap-sm login-brand">
            <CyberCubLogo size={54} className="brand-mark" />
            <span className="brand-name">
              Cyber<span className="brand-name-alt">Cub</span>
            </span>
          </div>

          <h1>
            Become a
            <br />
            <span className="hl">Cyber Hero!</span>
          </h1>
          <p className="login-lede">
            Learn how to spot tricks, build strong passwords and keep your world safe online —
            with games, stories and challenges made just for you.
          </p>

          <div className="login-features">
            <div className="feat"><span aria-hidden="true">🌐</span> How the internet really works</div>
            <div className="feat"><span aria-hidden="true">🪪</span> Your data &amp; your privacy</div>
            <div className="feat"><span aria-hidden="true">🪝</span> Spot scams, viruses &amp; fakes</div>
            <div className="feat"><span aria-hidden="true">🎨</span> Build your own safety stickers</div>
          </div>

          <CubMascot avatar="cub-grape" mood="wave" size={150} className="login-cub anim-float" />
        </section>

        {/* ---------- form side ---------- */}
        <section className="login-card card">
          <h2 className="tc">Welcome back!</h2>
          <p className="tc muted" style={{ marginBottom: '1.5rem' }}>
            Sign in with your email, or the User ID your teacher gave you.
          </p>

          {error && <Alert kind="bad" onDismiss={() => setError('')}>{error}</Alert>}

          {needsCode ? (
            <form onSubmit={onSubmitCode} noValidate>
              <div className="totp-step">
                <span className="totp-icon" aria-hidden="true">{useRecovery ? '🔑' : '📱'}</span>
                <p>
                  {useRecovery
                    ? 'Enter one of the recovery codes you saved when you turned this on.'
                    : 'Open your authenticator app and enter the 6-digit code for CyberCub.'}
                </p>
              </div>

              <div className={codeField.cls('code')}>
                <label htmlFor="code">
                  {useRecovery ? 'Recovery code' : '6-digit code'}
                </label>
                <input
                  id="code"
                  name="code"
                  type="text"
                  inputMode={useRecovery ? 'text' : 'numeric'}
                  autoComplete="one-time-code"
                  autoCapitalize="characters"
                  autoCorrect="off"
                  spellCheck="false"
                  placeholder={useRecovery ? 'ABCD-EFGH' : '123456'}
                  className="totp-input"
                  value={code}
                  onChange={(e) => {
                    const next = e.target.value;
                    setCode(next);
                    codeField.recheck({ code: next, useRecovery });
                  }}
                  maxLength={useRecovery ? 9 : 6}
                  required
                  autoFocus
                  {...codeField.flag('code')}
                />
                {codeField.error('code')}
              </div>

              <button
                type="submit"
                className={`btn btn-lg ${busy ? 'is-busy' : ''}`}
                style={{ width: '100%' }}
                aria-busy={busy}
                disabled={busy}
              >
                {busy ? (<><Spinner />Checking…</>) : 'Verify'}
              </button>

              <div className="row spread gap-sm" style={{ marginTop: '0.9rem' }}>
                <button
                  type="button"
                  className="link-button"
                  onClick={() => {
                    setUseRecovery((v) => !v); setCode(''); setError(''); codeField.clear();
                  }}
                >
                  {useRecovery ? 'Use my authenticator app' : 'Lost your phone?'}
                </button>
                <button
                  type="button"
                  className="link-button"
                  onClick={() => {
                    setNeedsCode(false); setCode(''); setError(''); codeField.clear();
                  }}
                >
                  Start again
                </button>
              </div>
            </form>
          ) : (
          <form onSubmit={onSubmit} noValidate>
            <div className={fields.cls('userId')}>
              <label htmlFor="userId">👤 Email or User ID</label>
              <input
                id="userId"
                name="userId"
                type="text"
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck="false"
                placeholder="you@example.com"
                value={userId}
                onChange={(e) => {
                  const next = e.target.value;
                  setUserId(next);
                  fields.recheck({ userId: next, password });
                }}
                required
                autoFocus
                {...fields.flag('userId')}
              />
              {fields.error('userId')}
              {/* One field on purpose: a child who signed up types their
                  email, a child whose school made the account types the
                  ID their teacher gave them, and both are looked up the
                  same way. type="text" rather than "email" so the second
                  kind is not told their own ID is invalid. */}
            </div>

            <div className={fields.cls('password')}>
              <label htmlFor="password">🔒 Password</label>
              <div className="password-wrap">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="Your secret password"
                  value={password}
                  onChange={(e) => {
                    const next = e.target.value;
                    setPassword(next);
                    fields.recheck({ userId, password: next });
                  }}
                  required
                  {...fields.flag('password')}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? '🙈' : '👀'}
                </button>
              </div>
              {fields.error('password')}
            </div>

            {/* Pressable whatever is in the boxes, and disabled only
                while it is working - the same bargain the sign-up form
                makes. A button that greys itself out cannot explain
                anything; a field marked red can. */}
            <button
              type="submit"
              className={`btn btn-lg ${busy ? 'is-busy' : ''}`}
              style={{ width: '100%' }}
              aria-busy={busy}
              disabled={busy}
            >
              {busy ? (<><Spinner />Checking…</>) : "Let's go! 🚀"}
            </button>

            {busy && (
              <p className="form-busy" role="status">
                Hang on — we are checking. You only need to press the button once.
              </p>
            )}
          </form>
          )}

          {/* No public sign-up: this just tells people where an account
              comes from, since there is nothing for them to create here. */}
          {!needsCode && (
            <div className="login-note">
              <strong>Joining with your school?</strong> Your teacher gives you a User ID and
              password — use those above. Ask them if you have forgotten it. If you already have
              your own account, sign in with the email address it uses.
            </div>
          )}

        </section>
      </div>

      <HelpBot />
    </div>
  );
}
